import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SessionService } from '@com-delta-idp/mnx-oauth';
import {
  AEMErrorMessagesSection,
  AEMFeatureFlags,
  CommonConstants,
  EndpointKeys,
  IDP_APPS,
  Libraries
} from '@com-delta-idp/mnx-skymiles-shared';
import {
  ApiBuilder,
  AppConfigService,
  handleError,
  HttpHeadersPayload,
  HttpRequestMethods
} from '@com-delta-idp/mnx-utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AEMMedallionBannerContent } from '../models/aem/medallion-banner.interfaces';
import { AEMMenuContent } from '../models/aem/menu.interfaces';
import { BUSINESS_TRAVELER_QUERY } from '../models/business-traveler.constants';
import {
  BusinessTravelerPayload,
  BusinessTravelerResponse,
  FeaturesFlag
} from '../models/business-traveler.interfaces';
import { Features } from '../models/features.interfaces';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  errorHandler = handleError.bind(this);

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private session: SessionService
  ) {}

  getMedallionBannerContent = (): Observable<AEMMedallionBannerContent> =>
    new ApiBuilder<undefined, AEMMedallionBannerContent>(
      this.http,
      this.appConfig
    )
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_LANDING_PAGE_MEDALLION_BANNER'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create();

  getMenuContent = (): Observable<AEMMenuContent> =>
    new ApiBuilder<undefined, AEMMenuContent>(this.http, this.appConfig)
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_LANDING_PAGE_MENU'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create();

  getFeatureFlags = (): Observable<AEMFeatureFlags<Features>> =>
    new ApiBuilder<undefined, AEMFeatureFlags<Features>>(
      this.http,
      this.appConfig
    )
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_LANDING_PAGE_FEATURE_FLAGS'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create();

  getErrorMessages = (): Observable<AEMErrorMessagesSection> =>
    new ApiBuilder<undefined, AEMErrorMessagesSection>(
      this.http,
      this.appConfig
    )
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_LANDING_PAGE_ERROR_MESSAGES'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create();

  getBusinessTravelerFeatureFlagAEMContent = (): Observable<boolean> =>
    new ApiBuilder<undefined, FeaturesFlag>(this.http, this.appConfig)
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_BUSINESS_TRAVELER_FEATURE_FLAGS'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create()
      .pipe(
        map((response: FeaturesFlag) => {
          return !!response?.flags?.overview;
        })
      );

  getBusinessTravelerContent = (): Observable<boolean> => {
    const headers: HttpHeadersPayload = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.session.token.access_token}`
    };

    return new ApiBuilder<BusinessTravelerPayload, BusinessTravelerResponse>(
      this.http,
      this.appConfig
    )
      .app(IDP_APPS.SKYMILES)
      .endpointKey(
        EndpointKeys['SKYMILES_API_BUSINESS_TRAVELER_CUSTOMER_PROFILE']
      )
      .method(HttpRequestMethods['POST'])
      .payload({ query: BUSINESS_TRAVELER_QUERY })
      .httpHeaders(headers)
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create()
      .pipe(
        map((response: BusinessTravelerResponse) => {
          return !!response?.data?.loyalty_program?.loyaltyMemberAccount
            ?.loyaltyMemberEligibility?.deltaBusinessTraveler;
        })
      );
  };
}
